import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
  useSlotGroupOverrides,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

/**
 * Full-width "View All Floor Plans" band for pages that spotlight a single
 * layout (e.g. the Featured Floor Plan page). Mirrors the structure and
 * styling of ScheduleATourCallout so these two bands feel like siblings
 * when used on the same page.
 */

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View All Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View All Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function ViewAllFloorPlansCallout() {
  const ctaLink = useSlotGroup('viewallfloorplanscallout.cta_link', ctaLinkDef);
  const slotGroupOverrides = useSlotGroupOverrides();

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background,#ffffff)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={sectionRef}
          className="reveal-fade-up vafp-card glow-trace-wrap relative text-center"
        >
          <GlowTrace delay={0.3} />
          <RichTextContent
            path="viewallfloorplanscallout.title"
            label="Title"
            defaultValue="<p>Not the Right Fit? Explore More Layouts</p>"
            as="h2"
            className="font-heading text-2xl sm:text-3xl md:text-4xl font-normal text-[var(--vibe-color-text)] mb-3 uppercase tracking-wide"
          />

          <RichTextContent
            path="viewallfloorplanscallout.subtitle"
            label="Subtitle"
            defaultValue='<p>Browse every floor plan at <span data-type="field-token" data-field="property_name"></span> to find the layout that fits you best.</p>'
            as="p"
            className="font-body text-base sm:text-lg text-[var(--vibe-color-text-muted)] max-w-2xl mx-auto mb-6"
          />

          <div className="w-16 h-1 mx-auto mb-6 rounded-full bg-[var(--vibe-color-primary)]/40" />

          <div className="flex justify-center">
            <EditableSlotGroup
              groupPath="viewallfloorplanscallout.cta_link"
              definition={ctaLinkDef}
              override={slotGroupOverrides['viewallfloorplanscallout.cta_link']}
            >
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="vafp-cta inline-flex items-center gap-2"
              >
                <span>{ctaLink.label || 'View All Floor Plans'}</span>
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .vafp-card {
          padding: 2rem 1.5rem 2.25rem;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 14px);
          background: #ffffff;
          box-shadow: 0 10px 36px rgba(0, 0, 0, 0.05);
        }
        @media (min-width: 768px) {
          .vafp-card { padding: 2.75rem 2rem 3rem; }
        }

        .vafp-cta {
          padding: 0.875rem 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
          border-radius: 9999px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .vafp-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ViewAllFloorPlansCallout',
  displayName: 'View All Floor Plans Callout',
  tokens: {
    'viewallfloorplanscallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Not the Right Fit? Explore More Layouts</p>',
    },
    'viewallfloorplanscallout.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Browse every floor plan at <span data-type="field-token" data-field="property_name"></span> to find the layout that fits you best.</p>',
    },
  },
  slotGroups: {
    'viewallfloorplanscallout.cta_link': ctaLinkDef,
  },
};

(ViewAllFloorPlansCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ViewAllFloorPlansCallout;
